import { DateTime } from 'luxon';

export const SEASONS = ['SPRING', 'SUMMER', 'FALL', 'WINTER'] as const;
export type Season = (typeof SEASONS)[number];

export const SEASON_MONTHS: Record<Season, { startMonth: number; endMonth: number }> = {
  SPRING: { startMonth: 3, endMonth: 5 },
  SUMMER: { startMonth: 6, endMonth: 8 },
  FALL: { startMonth: 9, endMonth: 11 },
  WINTER: { startMonth: 12, endMonth: 2 },
};

// Inclusive day-of-month for each season's last day. Winter uses 29 so leap-year
// Feb 29 is still in range; non-leap years just never reach the bound.
export const SEASON_END_DAY: Record<Season, number> = {
  SPRING: 31,
  SUMMER: 31,
  FALL: 30,
  WINTER: 29,
};

/** Default MM-DD bounds for a season — used when the shef doesn't narrow. */
export function getDefaultSeasonMonthDays(season: Season): { startMonthDay: string; endMonthDay: string } {
  const { startMonth, endMonth } = SEASON_MONTHS[season];
  const endDay = SEASON_END_DAY[season];
  return {
    startMonthDay: `${String(startMonth).padStart(2, '0')}-01`,
    endMonthDay: `${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`,
  };
}

/** Returns the season that contains `date`. Defaults to `DateTime.now()` for "what season is it right now" callers. */
export function getCurrentSeason(date: DateTime = DateTime.now()): Season {
  const { month } = date;
  if (month >= 3 && month <= 5) return 'SPRING';
  if (month >= 6 && month <= 8) return 'SUMMER';
  if (month >= 9 && month <= 11) return 'FALL';
  return 'WINTER';
}

/**
 * Returns the upcoming season if it begins within `lookAheadDays`, otherwise the
 * current season. Used to default seasonal UIs (e.g., the modal) to the season
 * shefs are about to plan for in the last weeks of the current season.
 */
export function getCurrentOrUpcomingSeason(now: DateTime = DateTime.now(), lookAheadDays = 14): Season {
  const currentSeason = getCurrentSeason(now);
  const upcomingSeason = getCurrentSeason(now.plus({ days: lookAheadDays }));
  return upcomingSeason !== currentSeason ? upcomingSeason : currentSeason;
}

export function isMonthInSeason(month: number, season: Season): boolean {
  if (season === 'WINTER') return month === 12 || month === 1 || month === 2;
  const { startMonth, endMonth } = SEASON_MONTHS[season];
  return month >= startMonth && month <= endMonth;
}

interface SeasonalAssignment {
  season: Season;
  seasonalWindowStart: string | null;
  seasonalWindowEnd: string | null;
}

/** Determines if a SEASONAL assignment is active for the delivery day. */
export function isSeasonalAssignmentActiveForDeliveryDay(
  assignment: SeasonalAssignment,
  deliveryDay: DateTime
): boolean {
  if (assignment.season !== getCurrentSeason(deliveryDay)) return false;
  if (assignment.seasonalWindowStart === null || assignment.seasonalWindowEnd === null) return true;
  const deliveryMonthDay = deliveryDay.toFormat('MM-dd');
  const narrowingStartMonthDay = assignment.seasonalWindowStart;
  const narrowingEndMonthDay = assignment.seasonalWindowEnd;
  if (narrowingStartMonthDay > narrowingEndMonthDay) {
    return deliveryMonthDay >= narrowingStartMonthDay || deliveryMonthDay <= narrowingEndMonthDay;
  }
  return deliveryMonthDay >= narrowingStartMonthDay && deliveryMonthDay <= narrowingEndMonthDay;
}

/**
 * The next concrete ISO date (YYYY-MM-DD) on or after `from` that this
 * SEASONAL row covers.
 *
 * A narrowed start of `02-29` is valid input even outside a leap year (the row is
 * annual and recurring), so the day is clamped to the target year's real month
 * length rather than passed straight to `DateTime.fromObject` — an unclamped `29`
 * in a common February builds an invalid DateTime whose `toISODate()` silently
 * returns `null` despite this function's `string` return type.
 */
export function nextSeasonalActiveDate(assignment: SeasonalAssignment, from: DateTime = DateTime.now()): string {
  const referenceDay = from.startOf('day');
  if (isSeasonalAssignmentActiveForDeliveryDay(assignment, referenceDay)) return referenceDay.toISODate();

  const firstActiveMonthDay =
    assignment.seasonalWindowStart ?? getDefaultSeasonMonthDays(assignment.season).startMonthDay;
  const [startMonth, startDay] = firstActiveMonthDay.split('-').map(Number);

  const candidateForYear = (year: number): DateTime => {
    const monthStart = DateTime.fromObject({ year, month: startMonth, zone: 'utc' }).startOf('month');
    return monthStart.set({ day: Math.min(startDay, monthStart.daysInMonth) });
  };

  const candidateThisYear = candidateForYear(referenceDay.year);
  const candidate = candidateThisYear < referenceDay ? candidateForYear(referenceDay.year + 1) : candidateThisYear;
  return candidate.toISODate();
}
